import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button, LinkedLogo, NamedLink } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';
import { FormattedMessage, useIntl } from 'react-intl';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const [isLangEn, setIsLangEn] = useState(false);
  const intl = useIntl();
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  useEffect(() => {
    const checkLanguage = localStorage.getItem('selectedLanguage');
    if (checkLanguage === 'en') {
      setIsLangEn(true);
    } else {
      setIsLangEn(false);
    }
  }, [isLangEn]);
  const isDutch = (intl?.locale || '').toLowerCase().startsWith('nl');
  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={classNames(css.content, getContentCss(numberOfColumns))}>
          <div className={css.logoNnewsletterForm}>
            <a href="/">
              <img className={css.logoWrapper} src="/static/images/white-logo.png" alt="logo" />
            </a>
            {/* <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            /> */}

            <div className={classNames(css.formContent, !isLangEn && css.moreFormContentWidth)}>
              <h6 className={css.headingFooter}>
                <FormattedMessage id="footer.formContentHeading" />
              </h6>
            </div>

            <div className={classNames(css.formContent, css.footerText, !isLangEn && css.moreFormContentWidth)}>
              <p>
                <FormattedMessage
                  id="footer.formcontentInfo"
                  values={{
                    highlight: chunks => <span>{chunks}</span>,
                    signupLink: chunks => (
                      <NamedLink name="SignupPage" className={css.signupLink}>
                        {chunks}
                      </NamedLink>
                    ),
                  }}
                />
              </p>
            </div>

            {/* <div className={css.formContainer}>
              <input placeholder={intl.formatMessage({ id: 'footer.formInputPlaceholder' })} />
              <Button className={css.submitNewsLetterBtn}>
                <FormattedMessage id="footer.formNewsLetterSubmitBtn" />
                <IconCard brand="paperPlain" />
              </Button>
            </div> */}
          </div>
          <div className={css.mobileMedia}>
            {showSocialMediaLinks ? (
              <div className={css.socialMediaContainer}>
                <h6 className={css.socialMediaLable}><FormattedMessage id='footer.socialMediaFollowLabel' /></h6>
                <div className={css.icons}>
                  <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
                </div>
              </div>
            ) : null}
          </div>
          <div className={css.sloganMobile}>
            {isLangEn ? (
              <Field data={slogan} className={css.slogan} />
            ) : (
              <div className={css.slogan}>
                <FormattedMessage id="footer.solgan" />
              </div>
            )
            }
          </div>
          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              {isLangEn ? (
                <Field data={slogan} className={css.slogan} />
              ) : (
                <div className={css.slogan}>
                  <FormattedMessage id="footer.solgan" />
                </div>
              )}

              {showSocialMediaLinks ? (
                <div className={css.socialMediaContainer}>
                  <h6 className={css.socialMediaLable}><FormattedMessage id='footer.socialMediaFollowLabel' /></h6>
                  <div className={css.icons}>
                    <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div className={classNames(css.grid, getGridCss(numberOfColumns))}>
            {isLangEn ? (
              <BlockBuilder blocks={blocks} sectionId={sectionId} options={options} />
            ) : (
              <>
                <div id="quick-links">
                  <nav>
                    <ul>
                      <li><FormattedMessage id="footer.quickLinksTitle" /></li>

                      <li>
                        <NamedLink name="NewListingPage" >
                          <FormattedMessage id="footer.link.submitVinyl" />
                        </NamedLink>
                      </li>

                      <li>
                        <NamedLink name="SearchPage">
                          <FormattedMessage id="footer.link.browseVinyl" />
                        </NamedLink>
                      </li>
                    </ul>
                  </nav>
                </div>
                <div id="company-links">
                  <nav>
                    <ul>
                      <li><FormattedMessage id="footer.companyTitle" /></li>
                      <li>
                        <NamedLink name="CMSPage" params={{ pageId: 'about' }}>
                          <FormattedMessage id="footer.link.about" />
                        </NamedLink>
                      </li>

                      <li>
                        <NamedLink name="CMSPage" params={{ pageId: isDutch ? 'how-it-work-nl' : 'how-it-work' }}>
                          <FormattedMessage id="footer.link.howItWorks" />
                        </NamedLink>
                      </li>

                      <li>
                        {isDutch ? (
                          <NamedLink name="CMSPage" params={{ pageId: 'nl-home' }} to={{ hash: '#home-faq-section' }}>
                            <FormattedMessage id="footer.link.faqs" />
                          </NamedLink>
                        ) : (
                          <NamedLink name="LandingPage" to={{ hash: '#home-faq-section' }}>
                            <FormattedMessage id="footer.link.faqs" />
                          </NamedLink>
                        )}
                      </li>
                      <li>
                        <NamedLink params={{ pageId: 'contact-us' }} name="CMSPage">
                          <FormattedMessage id="footer.link.contact" />
                        </NamedLink>
                      </li>
                      <li>
                        <NamedLink name="CMSPage" params={{ pageId: 'privacy-policy' }}>
                          <FormattedMessage id="footer.link.privacyPolicy" />
                        </NamedLink>
                      </li>
                      <li>
                        <NamedLink name="CMSPage" params={{ pageId: 'terms-of-service' }}>
                          <FormattedMessage id="footer.link.termsConditions" />
                        </NamedLink>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={css.copyrightText}>
          {
            isLangEn ?
              <Field data={copyright} className={css.copyright} />
              : <span className={css.copyright}>
                © 2026 GrooveBay. Alle rechten voorbehouden.
              </span>
          }
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
